/**
 * The thin chrome every staff shell wears: mode name, branch, sync pill, user.
 *
 * It is deliberately 44px tall and monochrome. A POS shell's real estate belongs
 * to the floor map / order grid / KDS board; the chrome's only job is to answer
 * "which terminal am I, am I online, who is signed in" at a glance.
 */

import { useState } from 'react';
import { Badge, Button, Drawer, OfflineBanner, Select } from '@sonauto/ui';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useDeviceMode } from '../device/DeviceModeProvider';
import { DEVICE_MODE_INFO, SELECTABLE_MODES } from '../device/mode';
import type { DeviceMode } from '../device/mode';
import { SyncStatusPill } from '../offline/SyncStatusPill';
import { useOutbox } from '../offline/OutboxProvider';

export interface ChromeProps {
  readonly title: ReactNode;
  readonly actions?: ReactNode;
  readonly onActivateDevice: () => void;
  readonly children: ReactNode;
  /** Dark chrome for wall-mounted screens (KDS, bar, queue board). */
  readonly dark?: boolean;
}

export function Chrome({ title, actions, onActivateDevice, children, dark = false }: ChromeProps): React.JSX.Element {
  const { me, signOut, switchBranch, branchId } = useAuth();
  const device = useDeviceMode();
  const outbox = useOutbox();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className={dark ? 'dark flex h-full flex-col bg-canvas text-fg' : 'flex h-full flex-col bg-canvas text-fg'}>
      <OfflineBanner
        online={outbox.snapshot.online}
        pendingCount={outbox.snapshot.pendingCount}
        onRetry={() => void outbox.flush()}
      />

      <header className="flex shrink-0 items-center gap-3 border-b border-line bg-surface px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-fg">{title}</div>
          <div className="truncate text-xs text-fg-subtle">
            {DEVICE_MODE_INFO[device.mode].nameVi}
            {' · '}
            {me?.branches.find((b) => b.id === branchId)?.name ?? 'Chưa chọn chi nhánh'}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <SyncStatusPill />
          <Button variant="ghost" size="sm" onClick={() => setMenuOpen(true)}>
            {me?.user.fullName ?? 'Tài khoản'}
          </Button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-hidden">{children}</main>

      <Drawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title="Thiết bị & tài khoản"
        side="right"
        footer={
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setMenuOpen(false)}>
              Đóng
            </Button>
            <Button variant="danger" onClick={() => void signOut()}>
              Đăng xuất
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <section>
            <h3 className="text-sm font-semibold text-fg">Người dùng</h3>
            <p className="text-flow text-sm text-fg-muted">
              {me?.user.fullName} ({me?.user.username})
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(me?.roles ?? []).map((role) => (
                <Badge key={`${role.code}-${role.branchId ?? 'all'}`} size="sm" tone="neutral">
                  {role.nameVi}
                </Badge>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg">Chi nhánh</h3>
            <Select
              className="mt-1"
              value={branchId ?? ''}
              onChange={(e) => void switchBranch(e.target.value)}
              placeholder="— Chọn chi nhánh —"
              options={(me?.branches ?? []).map((b) => ({ value: b.id, label: b.name }))}
            />
          </section>

          <section>
            <h3 className="text-sm font-semibold text-fg">Chế độ thiết bị</h3>
            <p className="text-flow mt-1 text-xs text-fg-subtle">
              Nguồn xác định:{' '}
              {device.source === 'REGISTERED_DEVICE'
                ? 'thiết bị đã đăng ký (máy chủ)'
                : device.source === 'LOCAL_OVERRIDE'
                  ? 'lựa chọn tại máy'
                  : device.source === 'PERMISSIONS'
                    ? 'suy ra từ quyền của người dùng'
                    : 'mặc định'}
              .
            </p>
            <Select
              className="mt-2"
              value={device.mode}
              onChange={(e) => device.setLocalMode(e.target.value as DeviceMode, device.stationId)}
              options={SELECTABLE_MODES.map((m) => ({ value: m, label: DEVICE_MODE_INFO[m].nameVi }))}
            />
            <p className="text-flow mt-2 text-xs text-fg-muted">
              Đổi ở đây chỉ áp dụng cho máy này. Nếu thiết bị đã được đăng ký kèm chế độ trên máy chủ, chế độ đó vẫn
              được ưu tiên sau khi tải lại.
            </p>
            <Button
              className="mt-3"
              variant="secondary"
              size="sm"
              onClick={() => {
                setMenuOpen(false);
                onActivateDevice();
              }}
            >
              Kích hoạt / gán lại thiết bị
            </Button>
          </section>
        </div>
      </Drawer>
    </div>
  );
}
